// src/components/charts/core/ChartSkeleton.tsx
/**
 * ChartSkeleton - Loading skeleton variants for ChartCard
 * 
 * Provides multiple skeleton variants for different content types:
 * - chart: Default chart skeleton with header and chart area
 * - stats: Grid of stat cards
 * - table: Table rows skeleton
 * - card: Generic card content skeleton
 */

import React from "react";

export type LoadingVariant = "chart" | "stats" | "table" | "card";

export interface ChartSkeletonProps {
  /** Skeleton variant type (default: 'chart') */
  variant?: LoadingVariant;
  /** Optional height constraint */
  height?: number | undefined;
}

const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ variant = "chart", height }) => {
  if (variant === "stats") {
    return (
      <div className="animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-24 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-32" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-700/20 rounded-lg p-3 space-y-2">
              <div className="h-2 bg-gray-700/40 rounded w-20" />
              <div className="h-6 bg-gray-700/50 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "table") {
    return (
      <div className="animate-pulse" style={height ? { minHeight: height } : undefined}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        <div className="flex gap-2 mb-2 pb-2 border-b border-gray-700/30">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`h-3 bg-gray-700/40 rounded ${i === 0 ? "w-24" : "flex-1"}`} />
          ))}
        </div>
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex gap-2 py-2">
              {[...Array(5)].map((_, j) => (
                <div key={j} className={`h-4 bg-gray-700/30 rounded ${j === 0 ? "w-24" : "flex-1"}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className="animate-pulse" style={height ? { minHeight: height } : undefined}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-700/40 rounded w-3/4" />
          <div className="h-4 bg-gray-700/30 rounded w-full" />
          <div className="h-4 bg-gray-700/30 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // Default 'chart' variant
  const chartHeight = height ?? 320;
  return (
    <div className="animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div>
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        <div className="h-8 bg-gray-700/30 rounded-lg w-32" />
      </div>
      <div className="relative w-full bg-gray-700/10 rounded-lg" style={{ height: chartHeight - 80 }} />
    </div>
  );
};

export default ChartSkeleton;

